import { useNavigate } from "react-router-dom";
import { Trophy, Medal, Award, ArrowRight, Check } from "lucide-react";
import PageShell from "./PageShell";
import { Reveal, ChapterHeading } from "../components/landing/Reveal";

const AWARDS = [
  { year: "2026", result: "Gold Winner", category: "Fitness Centres & Gyms", icon: Trophy, tone: "text-yellow-400" },
  { year: "2026", result: "Silver Winner", category: "Personal Trainer", icon: Medal, tone: "text-slate-300" },
  { year: "2025", result: "Gold Winner", category: "Personal Trainer", icon: Trophy, tone: "text-yellow-400" },
  { year: "2025", result: "Silver Winner", category: "Fitness Centres & Gyms", icon: Medal, tone: "text-slate-300" },
  { year: "2024", result: "Gold Winner", category: "Fitness Centres & Gyms", icon: Trophy, tone: "text-yellow-400" },
  { year: "2024", result: "Bronze Winner", category: "Personal Trainer", icon: Medal, tone: "text-amber-600" },
];

const AwardsPage = () => {
  const navigate = useNavigate();

  return (
    <PageShell title="Awards & Certificates">
      <section className="py-20 sm:py-28 bg-mint" data-testid="awards-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <ChapterHeading number="07" label="Recognition" title="Awards & Certificates" />
            <Reveal delay={0.15}>
              <p className="max-w-sm text-charcoal/70 leading-relaxed lg:text-right">
                Voted by the Red Deer community — three years running. Thank you for putting your
                trust in Me 1st Fitness &amp; Coaching.
              </p>
            </Reveal>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <Reveal className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -bottom-5 -left-5 w-full h-full rounded-3xl bg-charcoal" aria-hidden="true" />
                <div className="relative overflow-hidden rounded-3xl shadow-[0_20px_50px_rgb(0,0,0,0.15)]">
                  <img
                    src="/photos/awards-2026.jpg"
                    alt="Tammy Porter holding the 2026 CommunityVotes Red Deer plaques — Gold Winner Fitness Centres and Gyms, Silver Winner Personal Trainer — with 2024 and 2025 plaques on the wall behind her"
                    className="w-full object-cover"
                    data-testid="awards-2026-image"
                  />
                </div>
                <span className="absolute top-4 right-4 rounded-full bg-brand text-charcoal text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2">
                  2026 Wins
                </span>
              </div>
            </Reveal>

            <div className="lg:col-span-6 grid sm:grid-cols-2 gap-4">
              {AWARDS.map((a, i) => (
                <Reveal key={`${a.year}-${a.category}`} delay={0.08 * i} y={24}>
                  <article
                    className="h-full rounded-2xl bg-charcoal border border-sand/10 p-6 hover:border-brand/60 hover:-translate-y-0.5 transition-[border-color,transform] duration-300"
                    data-testid={`award-card-${i}`}
                  >
                    <a.icon size={26} className={a.tone} />
                    <p className="mt-4 text-[11px] uppercase tracking-[0.2em] font-bold text-sand/40">
                      CommunityVotes Red Deer {a.year}
                    </p>
                    <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-sand">
                      {a.result}
                    </h3>
                    <p className="mt-1 text-sm text-sand/60">{a.category}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center mt-14">
            <Reveal className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="overflow-hidden rounded-3xl shadow-[0_20px_50px_rgb(0,0,0,0.12)]">
                  <img
                    src="/photos/awards-2025.jpg"
                    alt="Tammy Porter holding the 2025 CommunityVotes Red Deer plaques — Gold Winner Personal Trainer, Silver Winner Fitness Centres and Gyms"
                    className="w-full h-full object-cover"
                    data-testid="awards-2025-image"
                  />
                </div>
                <div className="overflow-hidden rounded-3xl shadow-[0_20px_50px_rgb(0,0,0,0.12)]">
                  <img
                    src="/photos/awards.jpg"
                    alt="CommunityVotes Red Deer 2024 award plaques — Gold Winner Fitness Centres and Gyms, Bronze Winner Personal Trainer"
                    className="w-full h-full object-cover"
                    data-testid="awards-2024-image"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="rounded-3xl bg-white border border-black/5 p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-brand/15 flex items-center justify-center">
                    <Award size={20} className="text-brand" />
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-charcoal">
                    Certificates
                  </h3>
                </div>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <figure>
                    <div className="overflow-hidden rounded-2xl border border-black/10">
                      <img
                        src="/photos/certification-aflca.jpg"
                        alt="Fitness Alberta (AFLCA) certification for Tammy Porter — Group Conditioning, Group Choreography and Older Adult Specialty Leader"
                        className="w-full object-cover"
                        data-testid="certificate-image"
                      />
                    </div>
                    <figcaption className="mt-2 text-xs text-charcoal/50 font-medium">
                      Fitness Alberta — AFLCA Certified
                    </figcaption>
                  </figure>
                  <figure>
                    <div className="overflow-hidden rounded-2xl border border-black/10">
                      <img
                        src="/photos/cert-bonefit.jpg"
                        alt="Bone Fit Basics Workshop certificate from Osteoporosis Canada for Tammy Porter, May 2023"
                        className="w-full object-cover"
                        data-testid="certificate-image-bonefit"
                      />
                    </div>
                    <figcaption className="mt-2 text-xs text-charcoal/50 font-medium">
                      Osteoporosis Canada — Bone Fit™ Trained
                    </figcaption>
                  </figure>
                </div>
                <p className="mt-5 text-xs uppercase tracking-[0.2em] font-bold text-charcoal/40">
                  Certifications
                </p>
                <ul className="mt-3 space-y-2" data-testid="certificate-list">
                  <li className="flex items-start gap-2 text-charcoal/75 text-sm">
                    <Check size={16} className="text-brand-dark mt-0.5 shrink-0" />
                    Group Conditioning Specialty Leader
                  </li>
                  <li className="flex items-start gap-2 text-charcoal/75 text-sm">
                    <Check size={16} className="text-brand-dark mt-0.5 shrink-0" />
                    Group Choreography Specialty Leader
                  </li>
                  <li className="flex items-start gap-2 text-charcoal/75 text-sm">
                    <Check size={16} className="text-brand-dark mt-0.5 shrink-0" />
                    Older Adult Specialty Leader
                  </li>
                  <li className="flex items-start gap-2 text-charcoal/75 text-sm">
                    <Check size={16} className="text-brand-dark mt-0.5 shrink-0" />
                    Bone Fit™ Trained — Osteoporosis Canada (2023)
                  </li>
                </ul>
                <p className="mt-4 text-xs text-charcoal/50">
                  Member of Fitness Leadership Canada · Valid through November 30, 2026
                </p>
                <button
                  onClick={() => navigate("/contact")}
                  data-testid="awards-cta-btn"
                  className="group mt-7 rounded-full bg-charcoal text-sand font-bold px-8 py-4 text-sm tracking-wide inline-flex items-center gap-2 hover:bg-brand hover:text-charcoal transition-colors duration-300"
                >
                  TRAIN WITH AN AWARD-WINNING COACH
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default AwardsPage;
